import { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Role } from '@cvut/profit-theses-common'

import { addNotification } from '../features/toastNotifications'
import { authRedirectToPathUtils, TestLoginUser, useAuthInfo, useTestLogin, useTestLoginUsers } from '../api/auth'
import { fill } from '../components/Button.style'
import LoadingUserInfoMessage from '../access-control/LoadingUserInfoMessage'
import PageHeader from '../components/PageHeader'
import pagePaths from './paths'
import Select from '../components/form/Select'
import TextInput from '../components/form/TextInput'
import { useUserInfo } from '../session/user-info'
import * as style from './TestLoginPage.style'


function uniq<T> (values: T[]): T[] {
  return [...new Set(values)]
}

function formatUserRoleLabels (user: TestLoginUser): string[] {
  const specOfficerRolePrefix = `${Role.SPEC_OFFICER_}-`
  const roleLabels = []
  const activeStudySpecializations = uniq(
    (user.studies ?? [])
      .filter(study => study.active)
      .map(study => study.specialization?.code)
      .filter((code): code is string => !!code)
  )

  roleLabels.push(...activeStudySpecializations.map(code => `Student ${code}`))

  if (user.roles.includes(Role.PROPONENT)) {
    roleLabels.push('Vedouci')
  }

  if (user.roles.includes(Role.REVIEWER)) {
    roleLabels.push('Oponent')
  }

  if (user.roles.includes(Role.FT_OFFICER)) {
    roleLabels.push('Referent SZZ')
  }

  roleLabels.push(
    ...user.roles
      .filter(role => role.startsWith(specOfficerRolePrefix))
      .map(role => `Referent ${role.substring(specOfficerRolePrefix.length)}`)
  )

  return uniq(roleLabels)
}

function formatUserOptionLabel (user: TestLoginUser): string {
  const name = [user.titlesPre, user.firstName, user.lastName, user.titlesPost]
    .filter(Boolean)
    .join(' ')
  const roles = formatUserRoleLabels(user).join(' / ')
  const userLabel = name
    ? `${name} (${user.username})`
    : user.username

  return roles ? `${userLabel} - ${roles}` : userLabel
}

const TestLoginPage = (): JSX.Element => {
  const [username, setUsername] = useState('')
  const [developerLogin, setDeveloperLogin] = useState('')
  const [authInfoStatus, loadAuthInfo] = useAuthInfo()
  const [testLoginUsersStatus, loadTestLoginUsers] = useTestLoginUsers()
  const [testLoginStatus, testLogin] = useTestLogin()
  const [userInfo, ,loadUserInfo] = useUserInfo()
  const history = useHistory()

  useEffect(() => {
    void loadAuthInfo()
  }, [])

  useEffect(() => {
    if (authInfoStatus.state === 'success' && !authInfoStatus.data?.testAuthenticationEnabled) {
      history.replace(pagePaths.home)
    }

    if (authInfoStatus.state === 'success') {
      console.info('[test-auth] authentication info loaded', authInfoStatus.data)
      if (authInfoStatus.data?.testAuthenticationEnabled) {
        void loadTestLoginUsers()
      }
    }

    if (authInfoStatus.state === 'error') {
      console.error('[test-auth] authentication info failed', {
        status: authInfoStatus.response?.status,
        problem: authInfoStatus.httpProblem,
      })
    }
  }, [authInfoStatus.state])

  useEffect(() => {
    if (testLoginUsersStatus.state === 'success' && !username) {
      setUsername(testLoginUsersStatus.data?.[0]?.username ?? '')
    }
  }, [testLoginUsersStatus.state])

  useEffect(() => {
    if (testLoginStatus.state === 'success') {
      console.info('[test-auth] login succeeded', {
        username,
        developerLogin: developerLogin || undefined,
      })
      loadUserInfo()
    }

    if (testLoginStatus.state === 'error') {
      console.error('[test-auth] login failed', {
        username,
        developerLogin: developerLogin || undefined,
        status: testLoginStatus.response?.status,
        problem: testLoginStatus.httpProblem,
      })
    }
  }, [testLoginStatus.state])

  useEffect(() => {
    if (userInfo.state === 'loaded') {
      const redirectTo = authRedirectToPathUtils.get() ?? pagePaths.home
      authRedirectToPathUtils.clear()

      addNotification({
        type: 'POSITIVE',
        message: 'Login successful',
      })
      console.info('[test-auth] user info loaded', {
        username: userInfo.person.username,
        roles: userInfo.person.roles,
      })
      history.replace(redirectTo)
    }
  }, [userInfo.state])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const payload = {
      username,
      developerLogin: developerLogin || undefined,
    }

    console.info('[test-auth] login started', {
      username: payload.username,
      developerLogin: payload.developerLogin,
    })

    void testLogin(payload)
  }

  const loading = authInfoStatus.state === 'loading'
    || testLoginUsersStatus.state === 'loading'
    || testLoginStatus.state === 'loading'
    || userInfo.state === 'loading'
  const setUsernameFromSelectEvent = (event: ChangeEvent<HTMLSelectElement>) => setUsername(event.currentTarget.value)
  const setDeveloperLoginFromEvent = (event: ChangeEvent<HTMLInputElement>) => (
    setDeveloperLogin(event.currentTarget.value)
  )
  const testLoginUsers = testLoginUsersStatus.state === 'success'
    ? testLoginUsersStatus.data ?? []
    : []
  const userSelectDisabled = loading || testLoginUsers.length === 0

  return (
    <article>
      <PageHeader title='Test server login' />
      {loading && <LoadingUserInfoMessage loaderColor='primary' />}
      <div className={style.content}>
        <form className={style.form} onSubmit={handleSubmit}>
          <TextInput
            labelText='Developer login'
            inputProps={{
              id: 'test-login-developer-login',
              name: 'developerLogin',
              value: developerLogin,
              onChange: setDeveloperLoginFromEvent,
              autoComplete: 'username',
            }}
          />
          <Select
            labelText='User login'
            extraClassNames={[style.userSelect]}
            selectProps={{
              id: 'test-login-username',
              name: 'username',
              value: username,
              onChange: setUsernameFromSelectEvent,
              required: true,
              disabled: userSelectDisabled,
            }}
          >
            {testLoginUsersStatus.state === 'loading' && (
              <option value=''>Loading users...</option>
            )}
            {testLoginUsersStatus.state !== 'loading' && testLoginUsers.length === 0 && (
              <option value=''>No users available</option>
            )}
            {testLoginUsers.map(user => (
              <option key={user.username} value={user.username}>
                {formatUserOptionLabel(user)}
              </option>
            ))}
          </Select>
          <div className={style.actions}>
            <button type='submit' className={fill} disabled={loading}>
              Log in
            </button>
          </div>
        </form>

        <aside className={style.guide} aria-labelledby='test-login-guide-title'>
          <h2 id='test-login-guide-title'>Test-auth role guide</h2>
          <p>
            This page is for development testing of thesis workflow changes. Choose a seeded user whose global role
            and thesis relation match the action you need to test. When creating new test data, the role alone is
            usually not enough: the thesis must also point to the same student, supervisor, reviewer, or specialization.
          </p>

          <div className={style.tableWrapper}>
            <table>
              <thead>
                <tr>
                  <th>Role to test</th>
                  <th>Login to use</th>
                  <th>Required setup for new theses</th>
                  <th>Use for</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Student</td>
                  <td><code>horakjul</code>, <code>chalumat</code></td>
                  <td>
                    Student must have an active <code>Study</code>; the thesis must point to that study via
                    <code> assigneeId</code>.
                  </td>
                  <td>Student views, assignment acceptance/rejection, and student-side submission steps.</td>
                </tr>
                <tr>
                  <td>Supervisor</td>
                  <td><code>jandozde</code></td>
                  <td>
                    User needs the supervisor/proponent role; the thesis must point to the person via
                    <code> supervisorId</code>.
                  </td>
                  <td>Creating assignments, editing supervised theses, and proposing reviewers.</td>
                </tr>
                <tr>
                  <td>Reviewer</td>
                  <td><code>strnabed</code></td>
                  <td>
                    User needs the reviewer role; for assigned-reviewer actions the thesis must point to the person
                    via <code>reviewerId</code>.
                  </td>
                  <td>Reviewer candidacy, accepting reviewer work, and submitting reviewer reports.</td>
                </tr>
                <tr>
                  <td>BIE-WSI specialization officer</td>
                  <td><code>habrmtom</code>, <code>makardan</code>, <code>jirutjak</code></td>
                  <td>
                    User needs the specialization-officer role for <code>BIE-WSI</code>; the thesis specialization
                    must also be <code>BIE-WSI</code>.
                  </td>
                  <td>Approving or returning BIE-WSI thesis assignments and reviewer candidates for rework.</td>
                </tr>
                <tr>
                  <td>Final thesis officer</td>
                  <td><code>kolar</code>, <code>kordikp</code></td>
                  <td>User needs the final-thesis-officer role.</td>
                  <td>Global office workflows that are not limited to one specialization.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </aside>
      </div>
    </article>
  )
}

export default TestLoginPage
